/**
 * Minimales, LOKAL gebündeltes SVG-Balkendiagramm (AP-4.5).
 *
 * Ersetzt die Ninox-ECharts-CDN-Injection (R-E-04/R-E-07,
 * `docs/ninox-analysis/10-ereignismeldung/03_logik.md`: „Lädt externes
 * Skript von cdn.jsdelivr.net — Sicherheitsrelevant, kein Offline-Betrieb
 * möglich"). Statt einer zusätzlichen npm-Chart-Bibliothek reicht für die
 * hier benötigten einfachen Balkendiagramme (Vergleich Ereignisart/
 * Arbeitsort) eine kleine, abhängigkeitsfreie SVG-Komponente — sie wird
 * von Vite ganz normal mitgebündelt (kein Laufzeit-Fetch, kein CDN, keine
 * Netzwerkabhängigkeit im Betrieb).
 */

export interface BarChartSeries {
  label: string
  farbe: string
  werte: number[]
}

export interface BarChartProps {
  kategorien: string[]
  serien: BarChartSeries[]
  hoehe?: number
}

export function BarChart({ kategorien, serien, hoehe = 220 }: BarChartProps) {
  const maxWert = Math.max(1, ...serien.flatMap((s) => s.werte))
  const breitePlot = 640
  const abstandLinks = 32
  const abstandUnten = 28
  const plotHoehe = hoehe - abstandUnten
  const gruppenBreite = breitePlot / Math.max(1, kategorien.length)
  const balkenBreite = gruppenBreite / (serien.length + 1)

  return (
    <svg
      viewBox={`0 0 ${breitePlot + abstandLinks + 8} ${hoehe}`}
      className="w-full"
      role="img"
      aria-label="Balkendiagramm Ereignisvergleich"
    >
      {/* Y-Achse */}
      <line
        x1={abstandLinks}
        y1={0}
        x2={abstandLinks}
        y2={plotHoehe}
        stroke="#cbd5e1"
        strokeWidth={1}
      />
      {/* X-Achse */}
      <line
        x1={abstandLinks}
        y1={plotHoehe}
        x2={breitePlot + abstandLinks}
        y2={plotHoehe}
        stroke="#cbd5e1"
        strokeWidth={1}
      />

      {kategorien.map((kategorie, kategorieIndex) => {
        const gruppenX = abstandLinks + kategorieIndex * gruppenBreite
        return (
          <g key={kategorie}>
            {serien.map((serie, serieIndex) => {
              const wert = serie.werte[kategorieIndex] ?? 0
              const balkenHoehe = (wert / maxWert) * (plotHoehe - 8)
              const x = gruppenX + serieIndex * balkenBreite + balkenBreite / 2
              const y = plotHoehe - balkenHoehe
              return (
                <g key={serie.label}>
                  <rect
                    x={x}
                    y={y}
                    width={balkenBreite * 0.8}
                    height={balkenHoehe}
                    fill={serie.farbe}
                    rx={2}
                  >
                    <title>
                      {kategorie} — {serie.label}: {wert}
                    </title>
                  </rect>
                  {wert > 0 && (
                    <text
                      x={x + (balkenBreite * 0.8) / 2}
                      y={y - 4}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#475569"
                    >
                      {wert}
                    </text>
                  )}
                </g>
              )
            })}
            <text
              x={gruppenX + gruppenBreite / 2}
              y={plotHoehe + 16}
              textAnchor="middle"
              fontSize={11}
              fill="#334155"
            >
              {kategorie}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export function BarChartLegende({ serien }: { serien: BarChartSeries[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
      {serien.map((s) => (
        <span key={s.label} className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.farbe }} />
          {s.label}
        </span>
      ))}
    </div>
  )
}
